import { APP_VERSION_HEADER, statusForErrorCode } from '@hb/contracts';
import { ApiClientError, toApiClientError } from './errors.js';
/** 426: the one refusal every build ever shipped has to recognise the same way. See `app.update_required` in `@hb/contracts`. */
const UPDATE_REQUIRED_STATUS = statusForErrorCode('app.update_required');
/**
 * The 401s that are an answer rather than an expired token: a password or a
 * second factor in the body was wrong. Changing the password and deleting the
 * account both check one while signed in.
 */
const CREDENTIAL_REFUSALS = new Set(['auth.invalid_credentials', 'auth.totp_invalid']);
const REFRESH_PATH = '/identity/auth/refresh';
function buildUrl(baseUrl, path, query) {
    const url = `${baseUrl.replace(/\/+$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
    if (!query)
        return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined)
            params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
}
/**
 * The HTTP client every SPA and the Capacitor app share.
 *
 * It exists to hold three things in one place rather than in each app:
 * the access-token header, the refresh-and-retry on expiry, and the mapping
 * from the API's error envelope onto `ApiClientError`.
 */
export class ApiClient {
    options;
    doFetch;
    /**
     * The in-flight refresh, if any.
     *
     * Without this, a screen that fires five requests at once on load would, when
     * the access token has expired, start five refreshes. Since refresh tokens
     * ROTATE, four of those would present an already-rotated token, which the
     * server treats as theft and responds to by revoking the whole session
     * family - logging the user out for doing nothing but opening a page.
     * Collapsing them into one shared promise is what prevents that.
     */
    refreshInFlight = null;
    constructor(options) {
        this.options = options;
        this.doFetch = options.fetch ?? ((input, init) => fetch(input, init));
    }
    get(path, options = {}) {
        return this.request('GET', path, options);
    }
    post(path, body, options = {}) {
        return this.request('POST', path, { ...options, body });
    }
    put(path, body, options = {}) {
        return this.request('PUT', path, { ...options, body });
    }
    // No `patch`, on purpose. o2switch's front end resets every PATCH before the
    // API sees it, and the browser can only report that as no network at all - so
    // partial updates are POSTs, and there is no method here to send one with.
    delete(path, options = {}) {
        return this.request('DELETE', path, options);
    }
    async request(method, path, options = {}) {
        return await parseBody(await this.raw(method, path, options));
    }
    /**
     * The `Response` itself, after the same token, refresh-and-retry and error
     * mapping every other call gets.
     *
     * It exists for exactly one endpoint: the chat stream, whose body is
     * server-sent events rather than JSON and has to be read a chunk at a time.
     * The alternative was for the SPA to call `fetch` itself, which would mean
     * exposing the access token outside this client - and the whole reason it
     * lives in a closure in `@hb/auth-client` is that nothing else should be able
     * to reach it. Parsing the SSE framing is still the caller's job; obtaining an
     * authenticated response is not.
     */
    async raw(method, path, options = {}) {
        const response = await this.send(method, path, options);
        // Before anything else, the 401 included: an outdated build is refused
        // whatever it asks, and a refresh it tried here would be refused too.
        if (response.status === UPDATE_REQUIRED_STATUS) {
            this.options.onUpdateRequired?.();
            throw await toApiClientError(response);
        }
        // A 401 on an authenticated call means the short-lived access token has
        // expired. Refresh once, then replay. Never on the refresh endpoint itself,
        // which would recurse forever.
        if (response.status === 401 && !options.anonymous && path !== REFRESH_PATH) {
            const refused = await toApiClientError(response);
            // Except a 401 about a credential the request itself carried: a password
            // or a second factor checked again before something irreversible. The
            // token was fine, so a refresh would only replay the same wrong guess -
            // and each one counts against the account's lockout.
            if (CREDENTIAL_REFUSALS.has(refused.code))
                throw refused;
            const outcome = await this.refresh();
            if (outcome === 'rejected') {
                this.options.onAuthenticationLost();
                throw refused;
            }
            // Nothing was refused, so nothing is torn down: the request fails the way
            // any request fails without a network, and the next one tries again.
            if (outcome === 'unreachable') {
                throw new ApiClientError({ code: 'network', status: 0, message: 'The session could not be renewed: the server was not reachable.' });
            }
            const retried = await this.send(method, path, options);
            if (retried.status === UPDATE_REQUIRED_STATUS)
                this.options.onUpdateRequired?.();
            if (!retried.ok)
                throw await toApiClientError(retried);
            return retried;
        }
        if (!response.ok)
            throw await toApiClientError(response);
        return response;
    }
    /** Exchanges the refresh token for a new access token. At most one at a time. */
    async refresh() {
        this.refreshInFlight ??= this.performRefresh().finally(() => {
            this.refreshInFlight = null;
        });
        return await this.refreshInFlight;
    }
    async performRefresh() {
        if (this.options.refreshSession)
            return await this.refreshThroughPlatform(this.options.refreshSession);
        let response;
        try {
            response = await this.send('POST', REFRESH_PATH, { anonymous: true, body: {} });
        }
        catch {
            return 'unreachable';
        }
        // An outdated build is not a verdict on the credential either: the session
        // is kept for the build that replaces this one.
        if (response.status === UPDATE_REQUIRED_STATUS) {
            this.options.onUpdateRequired?.();
            return 'unreachable';
        }
        // Busy or broken is not a verdict on the credential. Everything else that is
        // not a success is one - a 401 most of all, but a 400 too: a refresh the
        // server cannot even read is not going to start working on the next try.
        if (response.status === 429 || response.status >= 500)
            return 'unreachable';
        if (!response.ok)
            return 'rejected';
        const tokens = (await response.json());
        this.options.onTokensRefreshed(tokens);
        return 'refreshed';
    }
    async refreshThroughPlatform(refreshSession) {
        let tokens;
        try {
            tokens = await refreshSession();
        }
        catch {
            return 'unreachable';
        }
        if (tokens === null)
            return 'rejected';
        this.options.onTokensRefreshed(tokens);
        return 'refreshed';
    }
    async send(method, path, options) {
        const headers = { accept: 'application/json' };
        if (options.body !== undefined)
            headers['content-type'] = 'application/json';
        // Merged before the credential, not after: a caller may replace `accept` -
        // which is the whole reason this option exists - but must not be able to
        // substitute an authorization header of its own.
        Object.assign(headers, options.headers);
        // On every request, anonymous ones included: signing in is exactly where an
        // outdated app has to be recognised.
        if (this.options.appVersion !== undefined)
            headers[APP_VERSION_HEADER] = this.options.appVersion;
        if (!options.anonymous) {
            const token = this.options.getAccessToken();
            if (token)
                headers.authorization = `Bearer ${token}`;
        }
        const init = {
            method,
            headers,
            // Required for the SSO refresh cookie on .honore-bagni.fr. Harmless for
            // native clients, which have no cookie jar.
            credentials: 'include',
        };
        // `rawBody` wins, and the two are never both meaningful: a caller sending a
        // file has nothing to serialise.
        if (options.rawBody !== undefined)
            init.body = options.rawBody;
        else if (options.body !== undefined)
            init.body = JSON.stringify(options.body);
        if (options.signal)
            init.signal = options.signal;
        try {
            return await this.doFetch(buildUrl(this.options.baseUrl, path, options.query), init);
        }
        catch (error) {
            // fetch rejects only for transport-level problems; give those the same
            // error type as everything else rather than leaking a TypeError.
            throw new ApiClientError({ code: 'network', status: 0, message: 'Network request failed', cause: error });
        }
    }
}
async function parseBody(response) {
    if (response.status === 204)
        return undefined;
    const text = await response.text();
    if (text.length === 0)
        return undefined;
    return JSON.parse(text);
}
export function createApiClient(options) {
    return new ApiClient(options);
}
//# sourceMappingURL=client.js.map